import { Typography } from "antd";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import type { FixtureStatuses } from "~enums/fixtureStatuses.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import type { SearchFilterParams } from "~pages/Persons/PersonListPage/types/searchFilterParams.ts";
import useFetchFixturePlayByPlayFlaggedEvents from "~pages/Reports/components/FixturePlayByPlayFlaggedEventDataTable/hooks/useFetchFixturePlayByPlayFlaggedEvents.ts";
import { useGetFixturePlayByPlayFlaggedEventDataTableColumns } from "~pages/Reports/components/FixturePlayByPlayFlaggedEventDataTable/hooks/useGetFixturePlayByPlayFlaggedEventDataTableColumns.tsx";
import { useGetFixturePlayByPlayFlaggedEventDataTableFilters } from "~pages/Reports/components/FixturePlayByPlayFlaggedEventDataTable/hooks/useGetFixturePlayByPlayFlaggedEventDataTableFilters.tsx";

const FixturePlayByPlayFlaggedEventDataTable = () => {
  const paginationConfig = useHandlePagination(50);
  const { sport } = useParams();
  const { formatMessage } = useIntl();
  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.reports.fixturePlayByPlayFlaggedEventList;
  const filterConfig = useGetFixturePlayByPlayFlaggedEventDataTableFilters();

  const listParams = useMemo<SearchFilterParams>(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
      fromTimestamp: filterConfig.currentFilter.dateRange?.from
        ? filterConfig.currentFilter.dateRange.from.startOf("day").format("YYYY-MM-DDTHH:mm:ss")
        : undefined,
      toTimestamp: filterConfig.currentFilter.dateRange?.to
        ? filterConfig.currentFilter.dateRange.to.endOf("day").format("YYYY-MM-DDTHH:mm:ss")
        : undefined,
      status: filterConfig.currentFilter.fixtureStatus
        ? filterConfig.currentFilter.fixtureStatus.map((status: { value: FixtureStatuses }) => status.value).join(",")
        : undefined,
      include: ["persons", "fixtures", "entities"],
    }),
    [paginationConfig, filterConfig],
  );
  const { data, isLoading, refetch } = useFetchFixturePlayByPlayFlaggedEvents(listParams, true);

  const columns = useGetFixturePlayByPlayFlaggedEventDataTableColumns();

  const dataSource = data?.data || [];

  return (
    <DataTable
      title={
        <Typography.Title level={4} style={{ fontWeight: 400 }}>
          {formatMessage({ id: "fixtures.play.by.play.flagged.events.title" })}
        </Typography.Title>
      }
      columns={columns}
      handleRefresh={() => {
        refetch();
      }}
      filtersConfig={filterConfig}
      isDataLoading={isLoading}
      rowKey={"eventId"}
      dataSource={dataSource}
      columnChooserLocalStorageKey={`${sport}.tables.settings.columns.fixturePlayByPlayFlaggedEvent`}
      permissionNames={[PermissionNames.FIXTURE_PLAY_BY_PLAY]}
      dataTableUiConfiguration={dataTableUiConfiguration}
      paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
    />
  );
};

export default FixturePlayByPlayFlaggedEventDataTable;
